import { CustomCalendarContainer } from "@components/plot/utils"
import { ImportantIcon } from "@helpers/ImportantIcon"
import { zodResolver } from "@hookform/resolvers/zod"
import * as Accordion from "@radix-ui/react-accordion"
import * as Toast from "@radix-ui/react-toast"
import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { useQueryClient } from "@tanstack/react-query"
import { Button, IconButton, LoadingCircle, Tooltip } from "@ui-lib/index"
import { requestApi2 } from "@utils/http"
import { portalSocket } from "@utils/websocket/portalSocket"
import { seempSocket } from "@utils/websocket/seempSocket"
import { seempSocket2 } from "@utils/websocket/seempSocket2"
import { endpoints, SeempAction, SeempActionGroup, SeempActionResult } from "api"
import { EventSourceManager } from "open-lib"
import {
    Dispatch,
    forwardRef,
    Fragment,
    InputHTMLAttributes,
    ReactElement,
    SetStateAction,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react"
import CurrencyInput from "react-currency-input-field"
import DatePicker from "react-datepicker"
import { Control, Controller, useForm, UseFormRegisterReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
    MdChevronLeft,
    MdClose,
    MdDelete,
    MdDone,
    MdEdit,
    MdEvent,
    MdOutlineLockPerson,
} from "react-icons/md"
import ReactSelect, { StylesConfig } from "react-select"
import { z } from "zod"
import { getHandlePlantCacheUpdated } from "./SeempContent"
import SeempPrevYearTrendPlot from "./SeempPrevYearTrendPlot"

const styles = (
	fieldErrorMessage: string | undefined,
	fieldValue: { label: string; value: string } | null,
	isDisabled: boolean
): StylesConfig => ({
	menu: (baseStyles) => ({
		...baseStyles,
		padding: "4px 4px 0px 4px",
	}),
	menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
	valueContainer: (baseStyles) => ({
		...baseStyles,
		padding: "0px 0px 0px 4px",
		height: 30,
	}),
	container: (baseStyles) => ({
		...baseStyles,
		":focus": { ring: 5, outline: "none" },
		padding: "0px 0px 0px 0px",
		height: 32,
	}),
	control: (baseStyles, state) => ({
		...baseStyles,
		fontSize: "12px",
		minHeight: 32,
		backgroundColor: isDisabled === true ? "white" : "white",
		height: 32,
		borderRadius: 2,
		boxShadow: state.isFocused ? "0 0 0 1px #4d82aa" : undefined,
		":hover": { borderColor: "#4d82aa", cursor: "pointer" },
		borderColor:
			fieldValue === null
				? "oklch(0.769 0.188 70.08)"
				: fieldErrorMessage !== undefined
					? "oklch(0.637 0.237 25.331)"
					: "#1c2b3933",
	}),
	input: (baseStyles) => ({
		...baseStyles,
		height: 20,
		padding: 0,
		fontSize: "12px",
	}),
	placeholder: (baseStyles) => ({
		...baseStyles,
		padding: 0,
		margin: "0px 0px 3px 4px",
		color: isDisabled === true ? "#1c2b39" : "#1c2b39",
	}),
	singleValue: (baseStyles) => ({
		...baseStyles,
		padding: 0,
		fontSize: "12px",
		margin: "0px 0px 2px 4px",
		color: isDisabled === true ? "#1c2b39" : "#1c2b39",
	}),
	multiValue: (baseStyles) => ({
		...baseStyles,
		padding: 0,
		margin: "0px 0px 2px 4px",
	}),
	multiValueLabel: (baseStyles) => ({
		...baseStyles,
		padding: 0,
		margin: 0,
	}),
	dropdownIndicator: (baseStyles) => ({
		...baseStyles,
		padding: 5,
		margin: 0,
	}),
	indicatorsContainer: (baseStyles) => ({
		...baseStyles,
		height: 32,
	}),
	clearIndicator: (baseStyles) => ({
		...baseStyles,
		padding: 5,
		margin: 0,
	}),
	option: (baseStyles) => ({
		...baseStyles,
		borderRadius: 2,
		padding: "4px 8px 8px 8px",
		fontSize: "12px",
		margin: 1,
		":hover": { cursor: "pointer" },
	}),
})

export type LockedActionList = {
	reportId: number
	actions: { actionId: number; userEmail: string; userName: string }[]
}[]

const SeempActionFormSchema = z.object({
	target: z.nullable(z.number()),
	howTo: z.nullable(z.string()),
	label: z.nullable(z.string()),
	responsible: z.nullable(z.object({ value: z.string(), label: z.string() })),
	investment: z.object({
		isSelected: z.boolean(),
		howTo: z.nullable(z.string()),
		deadline: z.nullable(z.date()),
		investmentAmount: z.nullable(z.number()),
	}),
	clientDialog: z.object({
		isSelected: z.boolean(),
		howTo: z.nullable(z.string()),
		deadline: z.nullable(z.date()),
	}),
	crewTraining: z.object({
		isSelected: z.boolean(),
		howTo: z.nullable(z.string()),
		deadline: z.nullable(z.date()),
	}),
})

export default function SeempActionCard({
	lockedActionList,
	plantId,
	reportId,
	actionData,
	usersList,
	handleAmountErrors,
	displayModeLabel = true,
	actionGroups,
}: {
	lockedActionList: LockedActionList
	plantId: number
	reportId: number
	actionData: SeempAction
	usersList: {
		name: string
		email: string
	}[]
	handleAmountErrors: (reportId: number, nErrors: number) => void
	displayModeLabel?: boolean
	actionGroups: SeempActionGroup[]
}) {
	const { t } = useTranslation()
	const tSeemp = useTranslation("seemp").t
	const [useWindowBottom, setUseWindowBottom] = useState(false)
	const actionContainerId = useId()

	useEffect(() => {
		const target = document.getElementById(actionContainerId)
		if (target === null) return

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry === undefined) return
				const rect = entry.boundingClientRect
				const viewportHeight = window.innerHeight
				const isBottomVisible = rect.bottom <= viewportHeight
				setUseWindowBottom(!isBottomVisible)
			},
			{
				threshold: 1.0,
			}
		)
		observer.observe(target)
		return () => observer.disconnect()
	}, [])

	const [seempProgress, setSeempProgress] = useState<
		| {
				actionId: number
				progressPercent: number
				result: SeempActionResult | undefined | null
		  }
		| undefined
	>()

	useEffect(() => {
		const eventManager = new EventSourceManager()

		eventManager.bind(seempSocket2.onSeempActionResult, (data) => {
			const id = data.actionId
			const progressPercent = 100
			const result = data.result

			if (id === actionData.id) {
				setSeempProgress({ actionId: id, progressPercent, result: result })
			}
		})

		return () => {
			eventManager.clear()
		}
	}, [])

	useEffect(() => {
		if (seempProgress?.progressPercent === 100) {
			handleUpdateActionProgressResult()
		}
		return () => {
			if (
				companyName !== undefined &&
				seempProgress?.progressPercent !== undefined &&
				seempProgress?.progressPercent !== 100
			) {
			}
		}
	}, [seempProgress?.progressPercent])

	const toastRef = useRef<HTMLOListElement>(null)

	const queryClient = useQueryClient()

	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)

	const {
		register,
		handleSubmit,
		formState: { isDirty, errors },
		control,
		reset,
		setValue,
		watch,
	} = useForm<z.infer<typeof SeempActionFormSchema>>({
		resolver: zodResolver(SeempActionFormSchema),
		defaultValues: {
			...actionData,
			responsible:
				actionData.responsible === null
					? null
					: {
							value: actionData.responsible,
							label: `${usersList.find((u) => u.email === actionData.responsible)?.name ?? actionData.responsible} (${actionData.responsible})`,
						},
		},
		mode: "onChange",
	})

	useEffect(() => {
		reset({
			...actionData,
			responsible:
				actionData.responsible === null
					? null
					: {
							value: actionData.responsible,
							label: `${usersList.find((u) => u.email === actionData.responsible)?.name ?? actionData.responsible} (${actionData.responsible})`,
						},
		})
	}, [actionData, reset])

	const userData = useAppSelector(getUserData)
	const companyName = useAppSelector(getCurrentCompany)

	const usersOptions = usersList?.map((user) => ({
		value: user.email,
		label: `${user.name} (${user.email})`,
	}))

	const currentReportActions =
		lockedActionList.find((reportData) => reportData.reportId === reportId)?.actions ?? []

	const [areFieldsDisabled, setAreFieldsDisabled] = useState(
		!currentReportActions.some((item) => item.actionId === actionData.id) ||
			currentReportActions.find((lockedAction) => lockedAction.actionId === actionData.id)
				?.userEmail !== userData.email
	)

	const [isBeingEditedByOther, setIsBeingEditedByOther] = useState(
		currentReportActions.find((lockedAction) => lockedAction.actionId === actionData.id)
			?.userEmail !== userData.email
	)

	useEffect(() => {
		setAreFieldsDisabled(
			!currentReportActions.some((item) => item.actionId === actionData.id) ||
				currentReportActions.find((lockedAction) => lockedAction.actionId === actionData.id)
					?.userEmail !== userData.email
		)
		setIsBeingEditedByOther(
			currentReportActions.some((item) => item.actionId === actionData.id) &&
				currentReportActions.find((lockedAction) => lockedAction.actionId === actionData.id)
					?.userEmail !== userData.email
		)
	}, [lockedActionList])

	const handleDelete = async () => {
		if (companyName === undefined) {
			alert("Company name is undefined")
			return
		}
		const confirmed = confirm(t("ARE YOU SURE YOU WANT TO DELETE THIS ACTION"))
		if (!confirmed) return

		const res = await requestApi2(endpoints.seempDeleteAction, {
			id: actionData.id,
		})
		if (res === null || res.success === false) {
			alert(t("THERE WAS AN ERROR DELETING THE ACTION"))
			return
		}
		seempSocket.unlockReportAction(
			companyName,
			plantId,
			reportId,
			actionData.id,
			userData.email
		)

		seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			companyName,
			plantId,
			"delete",
			undefined,
			reportId,
			actionData.id
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "delete",
			data: undefined,
			reportId: reportId,
			actionId: actionData.id,
		})
	}

	const onSubmit = async (data: z.infer<typeof SeempActionFormSchema>) => {
		if (companyName === undefined) {
			alert("Company name is undefined")
			return
		}
		const res = await requestApi2(endpoints.seempUpdateAction, {
			...data,
			responsible: data.responsible?.value ?? null,
			actionId: actionData.id,
			socketId: portalSocket.getSocketId(),
		})
		if (res === null) {
			alert(t("THERE WAS AN ERROR UPDATING THE ACTION"))
			return
		}
		seempSocket.unlockReportAction(
			companyName,
			plantId,
			reportId,
			actionData.id,
			userData.email
		)
		const newActionData: SeempAction = {
			...actionData,
			...data,
			responsible: data.responsible?.value ?? null,
			updatedAt: res.updatedAt,
		}
		seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			companyName,
			plantId,
			"update",
			newActionData,
			reportId,
			actionData.id
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "update",
			data: newActionData,
			reportId: reportId,
			actionId: actionData.id,
		})
	}

	const handleUpdateActionProgressResult = async () => {
		if (
			seempProgress?.progressPercent === 100 &&
			seempProgress.result !== null &&
			seempProgress.result !== undefined
		) {
			if (companyName === undefined) {
				alert("Company name is undefined")
				return
			}

			const newActionData: SeempAction = {
				...actionData,
				result: seempProgress.result,
				resultIsComputed: true,
			}

			seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
			seempSocket.sendPlantCacheUpdateRequest(
				companyName,
				plantId,
				"update",
				newActionData,
				reportId,
				actionData.id
			)
			handlePlantCacheUpdated({
				plantId,
				updateType: "update",
				data: newActionData,
				reportId: reportId,
				actionId: actionData.id,
			})
		}
	}

	// Merge socket-computed result with incoming actionData for immediate UI update
	const mergedActionData: SeempAction = useMemo(() => {
		return seempProgress?.result !== undefined && seempProgress.result !== null
			? { ...actionData, result: seempProgress.result, resultIsComputed: true }
			: actionData
	}, [actionData, seempProgress?.result])

	const target = watch("target")
	const howTo = watch("howTo")
	const label = watch("label")
	const responsible = watch("responsible")

	const isInvestmentChecked = watch("investment.isSelected")
	const investmentHowTo = watch("investment.howTo")
	const investmentDeadline = watch("investment.deadline")
	const investmentAmount = watch("investment.investmentAmount")

	const isClientDialogChecked = watch("clientDialog.isSelected")
	const clientDialogHowTo = watch("clientDialog.howTo")
	const clientDialogDeadline = watch("clientDialog.deadline")

	const isCrewTrainingChecked = watch("crewTraining.isSelected")
	const crewTrainingHowTo = watch("crewTraining.howTo")
	const crewTrainingDeadline = watch("crewTraining.deadline")

	const actionIdGroupMap: { type: string[]; label: string }[] = []
	for (const group of actionGroups) {
		const type = group.actionsDefinitions.map((def) => def.type)

		actionIdGroupMap.push({
			type: type,
			label: group.label,
		})
	}

	const groupLabel = actionIdGroupMap.find((group) =>
		group.type.includes(actionData.actionType)
	)?.label

	const displayName = actionData.actionType.includes(":")
		? tSeemp(`${groupLabel}.${actionData.actionType.split(":").slice(1).join(":").trimStart()}`)
		: t(actionData.actionType)

	const nWarnings =
		displayName !== "custom"
			? [
					responsible,
					target,
					howTo,
					...(isInvestmentChecked === true
						? [investmentHowTo, investmentDeadline, investmentAmount]
						: []),
					...(isClientDialogChecked === true
						? [clientDialogHowTo, clientDialogDeadline]
						: []),
					...(isCrewTrainingChecked === true
						? [crewTrainingHowTo, crewTrainingDeadline]
						: []),
				].filter((v) => v === null).length
			: [
					responsible,
					howTo,
					label,
					...(isInvestmentChecked === true
						? [investmentHowTo, investmentDeadline, investmentAmount]
						: []),
					...(isClientDialogChecked === true
						? [clientDialogHowTo, clientDialogDeadline]
						: []),
					...(isCrewTrainingChecked === true
						? [crewTrainingHowTo, crewTrainingDeadline]
						: []),
				].filter((v) => v === null).length

	useEffect(() => {
		handleAmountErrors(actionData.id, nWarnings)
	}, [nWarnings])

	const targetUnit =
		actionGroups
			.flatMap((group) => group.actionsDefinitions)
			.find((action) => action.type === actionData.actionType)?.unit ?? ""

	const description =
		actionGroups
			.flatMap((group) => group.actionsDefinitions)
			.find((action) => action.type === actionData.actionType)?.description ?? ""

	const modeDescription =
		actionGroups.find((group) =>
			group.actionsDefinitions.some((action) => action.type === actionData.actionType)
		)?.description ?? ""

	const [unitWidth, setUnitWidth] = useState(0)
	const measuredRef = useCallback((node: HTMLSpanElement | null) => {
		if (node !== null) {
			setUnitWidth(node.offsetWidth)
		}
	}, [])

	const [createdByWidth, setCreatedByWidth] = useState(0)
	const measuredCreatedByRef = useCallback((node: HTMLSpanElement | null) => {
		if (node !== null) {
			setCreatedByWidth(node.offsetWidth)
		}
	}, [])

	const [editedByWidth, setEditedByWidth] = useState(0)
	const measuredEditedByRef = useCallback((node: HTMLSpanElement | null) => {
		if (node !== null) {
			setEditedByWidth(node.offsetWidth)
		}
	}, [])

	const [isToastOpen, setIsToastOpen] = useState(false)
	const [toastType, setToastType] = useState<"notInEditMode" | "notChecked">("notInEditMode")

	// const isResultComputed = actionData.resultIsComputed

	// const socketDone = seempProgress?.progressPercent === 100
	const isDone = true
	// isResultComputed || socketDone || actionData.result !== null
	// 	? true
	// 	: displayName === "custom"
	// 		? true
	// 		: false

	return (
		<div
			className={`relative`}
			id={actionContainerId}
		>
			{companyName === undefined ? (
				<div
					className={`${displayModeLabel === true ? "shadow-card" : ""} grid w-full grid-cols-[0.75rem_1fr_4.5rem] gap-3 overflow-hidden rounded-sm border-2 bg-white outline-none`}
				>
					<div className={`mr-1 h-full w-2 animate-pulse bg-gray-300`} />
					<div className={`flex h-full flex-col gap-1 py-3 text-left`}>
						<div
							className={`h-[calc(1.5rem-0.5rem/3)] animate-pulse rounded-lg bg-gray-300`}
							style={{ width: (Math.random() * (1 / 5 - 1 / 6) + 1 / 6) * 100 + "%" }}
						/>
						<div
							className={`h-[calc(1.125rem-0.5rem/3)] animate-pulse rounded-lg bg-gray-300`}
							style={{ width: (Math.random() * (4 / 5 - 2 / 5) + 2 / 5) * 100 + "%" }}
						/>
						<div
							className={`h-[calc(1.125rem-0.5rem/3)] animate-pulse rounded-lg bg-gray-300`}
							style={{ width: (Math.random() * (2 / 5 - 1 / 5) + 1 / 5) * 100 + "%" }}
						/>
					</div>
					<div className={`flex items-center justify-center`}>
						<MdChevronLeft
							className="h-10 w-10 rotate-180 animate-pulse text-gray-300 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)]"
							aria-hidden
						/>
					</div>
				</div>
			) : (
				<>
					{isBeingEditedByOther === true ||
					currentReportActions.some(
						(item) =>
							item.userEmail === userData.email && item.actionId !== actionData.id
					) ? (
						<Tooltip
							sideOffset={-2}
							align={"end"}
							// collisionBoundary={commentsDivRef}
							arrowClassName="hidden fill-dipai-secondary-700/0 stroke-white/0"
							className={
								"bg-dipai-secondary-100 shadow-card text-headline border-dipai-secondary-700 text-dipai-secondary-901 border-1"
							}
							customContent={
								<p className={`text-tiny`}>
									{currentReportActions.some(
										(item) =>
											item.userEmail === userData.email &&
											item.actionId !== actionData.id
									)
										? t("YOU ARE CURRENTLY EDITING ANOTHER ACTION")
										: `${t("CURRENTLY BEING EDITED BY")} ${
												usersList.find(
													(u) =>
														u.email ===
														currentReportActions.find(
															(lockedAction) =>
																lockedAction.actionId ===
																actionData.id
														)?.userEmail
												)?.name ??
												currentReportActions.find(
													(lockedAction) =>
														lockedAction.actionId === actionData.id
												)?.userEmail
											}`}
								</p>
							}
						>
							<div
								className={`border-alert-focus shadow-card absolute -top-3 -right-3 flex h-7 w-7 items-center justify-center rounded-full border-1 bg-white p-1`}
							>
								<MdOutlineLockPerson
									className="text-dipai-secondary-800 h-5 w-5 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-hover:cursor-pointer"
									aria-hidden
								/>
							</div>
						</Tooltip>
					) : null}
					<Accordion.Item
						value={`${actionData.id}`}
						className={`${displayModeLabel === true ? "shadow-card" : ""} overflow-hidden rounded-sm bg-white ${
							isBeingEditedByOther === true
								? "border-alert-focus border-2 border-dashed"
								: currentReportActions.some(
											(item) =>
												item.userEmail === userData.email &&
												item.actionId !== actionData.id
									  )
									? "border-alert-focus border-2 border-dashed"
									: "border-2"
						}`}
					>
						<Accordion.Header className="flex min-h-21">
							<Accordion.Trigger asChild>
								<div
									className={
										"group hover:bg-dipai-secondary-800/5 data-[state=open]:bg-dipai-secondary-800/5 grid w-full grid-cols-[0.75rem_1fr_4.5rem] gap-3 outline-none"
									}
									role="button"
									tabIndex={0}
									onKeyDown={(e) => {
										// Preserve keyboard toggle for Enter/Space when not focusing an input
										const isInteractiveTarget =
											(e.target as HTMLElement).tagName === "INPUT" ||
											(e.target as HTMLElement).tagName === "TEXTAREA"
										if (isInteractiveTarget) {
											// Do not toggle accordion when typing inside inputs
											return
										}
										if (e.key === " " || e.key === "Enter") {
											// Let Radix handle via click simulation
										}
									}}
								>
									<div
										className={`mr-1 h-full w-2 ${nWarnings > 0 ? `bg-amber-600` : ""}`}
									/>
									<div className={`flex h-full flex-row gap-3 py-3 text-left`}>
										{isDone !== true ? (
											<>
												<LoadingCircle size="sm" />
											</>
										) : null}

										<div className={`flex h-full flex-col py-3 text-left`}>
											<div
												className={`text-dipai-secondary-901 text-menu font-semibold first-letter:uppercase`}
											>
												{displayModeLabel === true
													? `${groupLabel}: `
													: null}
												<span className={`font-normal`}>
													{displayName !== "custom" ? (
														<>
															{displayName.includes("XX") ? (
																<>
																	{displayName
																		.split("XX")
																		.map((part, idx, arr) => (
																			<Fragment key={idx}>
																				{part}
																				{idx <
																					arr.length -
																						1 && (
																					<span className="font-semibold text-green-600">
																						{actionData.target !==
																						null
																							? actionData.target
																							: "--"}
																					</span>
																				)}
																			</Fragment>
																		))}
																</>
															) : (
																displayName
															)}
														</>
													) : (
														<>
															{/* <span>{`${actionData.label ?? "unnamed"}`}</span> */}
															{areFieldsDisabled !== true ? (
																<InputContainer
																	id="custom-label"
																	label={t("TITLE")}
																	fillHeight={false}
																>
																	<input
																		type="text"
																		readOnly={areFieldsDisabled}
																		id="custom-label"
																		{...register("label", {
																			setValueAs: (v) =>
																				v === "" ? null : v,
																		})}
																		onClick={(e) => {
																			// Prevent click bubbling to Accordion.Trigger which would toggle the accordion
																			e.stopPropagation()
																			e.nativeEvent.stopImmediatePropagation()
																			if (areFieldsDisabled)
																				setIsToastOpen(true)
																		}}
																		onKeyDown={(e) => {
																			// Prevent Space from toggling accordion while typing
																			if (e.key === " ") {
																				e.preventDefault() // avoid triggering click on trigger
																			}
																			e.stopPropagation()
																			// Narrow native event to KeyboardEvent to safely access method
																			const nativeEvt =
																				e.nativeEvent as KeyboardEvent
																			;(
																				nativeEvt as unknown as {
																					stopImmediatePropagation?: () => void
																				}
																			).stopImmediatePropagation?.()
																		}}
																		onKeyDownCapture={(e) => {
																			// Extra safety in capture phase
																			e.stopPropagation()
																		}}
																		className={`${label === null ? "border-amber-500" : errors.label !== undefined ? "border-red-500" : "border-dipai-secondary-901/20"} ${areFieldsDisabled ? "cursor-help" : ""} text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 rounded-sm border-1 p-2`}
																	/>
																</InputContainer>
															) : (
																<span className="first-letter:uppercase">{`${actionData.label ?? "unnamed"}`}</span>
															)}
															{label === null ? (
																<ValidationMessage
																	type={"warning"}
																	validationMessage={t(
																		"PROVIDE A TITLE"
																	)}
																/>
															) : null}
															{errors.label?.message !== undefined ? (
																<ValidationMessage
																	type={"error"}
																	validationMessage={
																		errors.label.message
																	}
																/>
															) : null}
														</>
													)}
													{nWarnings > 0 ? (
														<span className={`text-amber-600`}>
															{` (${nWarnings} ${nWarnings === 1 ? t("INPUT REQUIRED").toLowerCase() : t("INPUTS REQUIRED").toLowerCase()})`}
														</span>
													) : null}{" "}
												</span>
											</div>
											<p
												className={`text-dipai-secondary-901/80 text-tiny leading-[1.125rem]`}
											>
												{tSeemp(`${groupLabel}.${description}`)}
											</p>
											<p
												className={`text-dipai-secondary-901/80 text-tiny leading-[1.125rem]`}
											>
												{displayName !== "custom"
													? tSeemp(`${groupLabel}.${modeDescription}`)
													: null}
											</p>
										</div>
									</div>
									<div className={`flex items-center justify-center`}>
										<MdChevronLeft
											className="text-dipai-secondary-901 group-hover:text-dipai-secondary-600 h-10 w-10 rotate-180 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-hover:scale-110 group-hover:cursor-pointer group-data-[state=open]:rotate-270"
											aria-hidden
										/>
									</div>
								</div>
							</Accordion.Trigger>
						</Accordion.Header>
						<Accordion.Content
							className={
								"data-[state=closed]:animate-slide-accordion-up data-[state=open]:animate-slide-accordion-down grid w-full grid-cols-[0.75rem_1fr] gap-3 overflow-hidden pr-6"
							}
						>
							<div
								className={`mr-1 h-[calc(100%+0.75rem)] w-2 ${nWarnings > 0 ? `bg-amber-600` : ""}`}
							/>
							<Toast.Provider swipeDirection="right">
								<div className={`flex flex-col gap-3 pt-3 pb-0`}>
									<div className={`flex w-full justify-between gap-3 pb-0`}>
										<div
											className={`flex flex-row items-center gap-2 border-l-1 border-black/0 pl-0`}
										>
											<label
												htmlFor="created-by"
												className={`text-tiny text-dipai-secondary-901 h-4`}
											>
												{t("CREATED BY")}
											</label>
											<Tooltip
												sideOffset={-2}
												alignOffset={createdByWidth / 2}
												align={"start"}
												// collisionBoundary={commentsDivRef}
												arrowClassName="hidden fill-dipai-secondary-700/0 stroke-white/0"
												className={
													"bg-dipai-secondary-100 text-headline shadow-card border-dipai-secondary-700 text-dipai-secondary-901 border-1"
												}
												customContent={
													<p className={`text-tiny`}>
														{actionData.createdBy}
													</p>
												}
											>
												<p
													ref={measuredCreatedByRef}
													className={`text-tiny text-dipai-secondary-901 w-fit`}
												>
													{usersList.find(
														(u) => u.email === actionData.createdBy
													)?.name ?? actionData.createdBy}
												</p>
											</Tooltip>
										</div>
										<div className={`flex flex-row gap-3`}>
											{currentReportActions.some(
												(item) =>
													item.userEmail === userData.email &&
													item.actionId !== actionData.id
											) ? (
												<div
													className={`flex flex-row items-center gap-2 border-l-1 border-black/0 pl-4`}
												>
													<p
														className={`text-dipai-secondary-901 text-tiny h-4 rounded-sm`}
													>
														{t(
															"YOU ARE CURRENTLY EDITING ANOTHER ACTION"
														)}
													</p>
												</div>
											) : areFieldsDisabled === true &&
											  currentReportActions.some(
													(item) => item.actionId === actionData.id
											  ) === false ? (
												<Button
													variant={"outline-dark"}
													onClick={handleDelete}
												>
													<Button.Icon>{MdDelete}</Button.Icon>
													<Button.Label>{t("DELETE")}</Button.Label>
												</Button>
											) : areFieldsDisabled === false ? (
												<Button
													variant={"outline-dark"}
													disabled={areFieldsDisabled}
													onClick={() => {
														reset({
															...actionData,
															responsible:
																actionData.responsible === null
																	? null
																	: {
																			value: actionData.responsible,
																			label: `${usersList.find((u) => u.email === actionData.responsible)?.name ?? actionData.responsible} (${actionData.responsible})`,
																		},
														})
														seempSocket.unlockReportAction(
															companyName,
															plantId,
															reportId,
															actionData.id,
															userData.email
														)
													}}
												>
													<Button.Icon>{MdClose}</Button.Icon>
													<Button.Label>{t("CANCEL")}</Button.Label>
												</Button>
											) : isBeingEditedByOther ||
											  currentReportActions.some(
													(item) =>
														item.userEmail === userData.email &&
														item.actionId !== actionData.id
											  ) ? (
												<div
													className={`flex flex-row items-center gap-2 border-l-1 border-black/0 pl-4`}
												>
													{currentReportActions.some(
														(item) =>
															item.userEmail === userData.email &&
															item.actionId !== actionData.id
													) ? (
														<p
															className={`text-dipai-secondary-901 text-tiny h-4 rounded-sm`}
														>
															{t(
																"YOU ARE CURRENTLY EDITING ANOTHER ACTION"
															)}
														</p>
													) : (
														<>
															<label
																htmlFor="being-edited-by"
																className={`text-tiny text-dipai-secondary-901 h-4`}
															>
																{t("CURRENTLY BEING EDITED BY")}
															</label>
															<Tooltip
																sideOffset={-2}
																alignOffset={editedByWidth / 2}
																align={"start"}
																// collisionBoundary={commentsDivRef}
																arrowClassName="hidden fill-dipai-secondary-700/0 stroke-white/0"
																className={
																	"bg-dipai-secondary-100 text-headline shadow-card border-dipai-secondary-700 text-dipai-secondary-901 border-1"
																}
																customContent={
																	<p className={`text-tiny`}>
																		{
																			currentReportActions.find(
																				(lockedAction) =>
																					lockedAction.actionId ===
																					actionData.id
																			)?.userEmail
																		}
																	</p>
																}
															>
																<p
																	id="being-edited-by"
																	ref={measuredEditedByRef}
																	className={`text-dipai-secondary-901 text-tiny h-4 rounded-sm`}
																>
																	{`${
																		usersList.find(
																			(u) =>
																				u.email ===
																				currentReportActions.find(
																					(
																						lockedAction
																					) =>
																						lockedAction.actionId ===
																						actionData.id
																				)?.userEmail
																		)?.name ??
																		currentReportActions.find(
																			(lockedAction) =>
																				lockedAction.actionId ===
																				actionData.id
																		)?.userEmail
																	}`}
																</p>
															</Tooltip>
														</>
													)}
												</div>
											) : null}
											{areFieldsDisabled === true ? (
												<Button
													className={`${isToastOpen && toastType === "notInEditMode" ? "animate-bounce" : ""}`}
													variant={"filled-secondary"}
													disabled={
														currentReportActions.some(
															(item) =>
																item.actionId === actionData.id
														) ||
														currentReportActions.some(
															(item) =>
																item.userEmail === userData.email
														)
													}
													onClick={() => {
														seempSocket.lockReportAction(
															companyName,
															plantId,
															reportId,
															actionData.id,
															{
																email: userData.email,
																name: userData.name,
															}
														)
													}}
												>
													{isBeingEditedByOther ||
													currentReportActions.some(
														(item) =>
															item.userEmail === userData.email &&
															item.actionId !== actionData.id
													) ? (
														<Button.Icon>
															{MdOutlineLockPerson}
														</Button.Icon>
													) : (
														<Button.Icon>{MdEdit}</Button.Icon>
													)}
													<Button.Label>{t("EDIT")}</Button.Label>
												</Button>
											) : (
												<Button
													disabled={isDirty === false}
													variant={"filled-secondary"}
													blockSubmit={false}
													onClick={() => handleSubmit(onSubmit)()}
												>
													<Button.Icon>{MdDone}</Button.Icon>
													<Button.Label>{t("UPDATE")}</Button.Label>
												</Button>
											)}
										</div>
									</div>
									{/* PERFORMANCE TARGET SECTION */}
									{
										displayName !== "custom" ? (
											<div
												className={`border-dipai-secondary-901/20 text-dipai-secondary-901 flex flex-col gap-2 rounded-sm border-1 p-4 pb-2`}
											>
												<div
													className={`flex flex-row justify-between gap-4`}
												>
													<p className={`text-button h-8 font-semibold`}>
														{t("PERFORMANCE TARGET")}
													</p>
													<div
														className={`flex w-auto flex-row items-start gap-16`}
													>
														<div className={`flex flex-row gap-0.5`}>
															<div
																className={`flex flex-row items-center justify-end gap-2`}
															>
																<label
																	htmlFor="performance-target"
																	className={`text-tiny text-dipai-secondary-901 h-4 w-20`}
																>
																	{t("ADJUST TARGET")}
																</label>
																<div className="relative inline-block">
																	{/* <input
																		type="text"
																		inputMode="decimal"
																		pattern="[0-9]*\\.?[0-9]*"
																		className={`${target === null ? "border-amber-500" : errors.target !== undefined ? "border-red-500" : "border-dipai-secondary-901/20"} ${areFieldsDisabled ? "cursor-help" : ""} hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 w-50 rounded-sm border-1 p-2 text-right`}
																		{...register("target", {
																			setValueAs: (
																				v: string | number
																			) => {
																				if (v === "") {
																					return null
																				}
																				if (
																					typeof v ===
																					"string"
																				) {
																					const onlyDigits =
																						v.replace(
																							/\D/g,
																							""
																						)
																					if (
																						isNaN(
																							Number(
																								onlyDigits
																							)
																						) === true
																					) {
																						return null
																					}
																					return Number(
																						onlyDigits
																					)
																				}
																				return v
																			},
																			onChange: (
																				e: React.ChangeEvent<HTMLInputElement>
																			) => {
																				const onlyDigits =
																					e.target.value.replace(
																						/\D/g,
																						""
																					)
																				if (
																					onlyDigits ===
																					""
																				) {
																					setValue(
																						"target",
																						null
																					)
																				} else {
																					if (
																						isNaN(
																							Number(
																								onlyDigits
																							)
																						) === true
																					) {
																						return
																					}
																					setValue(
																						"target",
																						Number(
																							onlyDigits
																						)
																					)
																				}
																			},
																		})}
																		style={{
																			paddingRight: `${unitWidth + 12}px`,
																		}}
																		onClick={(e) => {
																			areFieldsDisabled &&
																				setIsToastOpen(true)
																		}}
																		readOnly={areFieldsDisabled}
																	/> */}
																	<input
																		type="text"
																		inputMode="decimal"
																		pattern="[0-9]*[.]?[0-9]*"
																		className={`${
																			target === null
																				? "border-amber-500"
																				: errors.target !==
																					  undefined
																					? "border-red-500"
																					: "border-dipai-secondary-901/20"
																		} ${areFieldsDisabled ? "cursor-help" : ""} hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 w-50 rounded-sm border p-2 text-right`}
																		{...register("target", {
																			setValueAs: (
																				v:
																					| string
																					| number
																					| null
																			) => {
																				if (
																					v === "" ||
																					v === null
																				)
																					return null
																				if (
																					typeof v ===
																					"number"
																				)
																					return Number.isFinite(
																						v
																					)
																						? v
																						: null

																				// Replace comma with dot, strip invalid chars
																				const cleaned = v
																					.replace(
																						/,/g,
																						"."
																					)
																					.replace(
																						/[^0-9.]/g,
																						""
																					)

																				// Ensure only one dot allowed
																				if (
																					!/^\d*\.?\d*$/.test(
																						cleaned
																					)
																				)
																					return null

																				const num =
																					Number(cleaned)
																				return Number.isNaN(
																					num
																				)
																					? null
																					: num
																			},
																			onChange: (
																				e: React.ChangeEvent<HTMLInputElement>
																			) => {
																				// Replace commas with dots in user input
																				const input =
																					e.target.value.replace(
																						/,/g,
																						"."
																					)
																				// Strip everything except digits and one dot
																				const cleaned =
																					input.replace(
																						/[^0-9.]/g,
																						""
																					)
																				if (
																					!/^\d*\.?\d*$/.test(
																						cleaned
																					)
																				)
																					return

																				// Update the actual form value as number or null
																				setValue(
																					"target",
																					cleaned === ""
																						? null
																						: Number(
																								cleaned
																							)
																				)

																				// Visually update the input field to show the replaced dot
																				e.target.value =
																					cleaned
																			},
																		})}
																		style={{
																			paddingRight: `${unitWidth + 12}px`,
																		}}
																		onClick={() => {
																			if (areFieldsDisabled)
																				setIsToastOpen(true)
																		}}
																		readOnly={areFieldsDisabled}
																	/>

																	<span
																		ref={measuredRef}
																		className="text-menu pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-gray-500"
																	>
																		{targetUnit}
																	</span>
																</div>
															</div>
															{target === null ? (
																<ValidationMessage
																	type={"warning"}
																	validationMessage={t(
																		"ASSIGN A PERFORMANCE TARGET"
																	)}
																/>
															) : null}
															{errors.target?.message !==
															undefined ? (
																<ValidationMessage
																	type={"error"}
																	validationMessage={
																		errors.target.message
																	}
																/>
															) : null}
														</div>
													</div>
												</div>

												<div
													className={`flex w-full flex-col gap-2 border-t-1 pt-2`}
												>
													<div
														className={`flex h-6 flex-row items-center justify-between gap-0`}
													>
														<p
															className={`text-tiny h-4 pr-40 font-semibold`}
														>
															{t("LAST YEAR TREND")}
														</p>
														<div
															className={`flex flex-row items-center gap-2`}
														>
															<label
																htmlFor="performance-last-year"
																className={`text-tiny text-dipai-secondary-901 h-4`}
															>
																{`${t("LAST YEAR PERFORMANCE")}:`}
															</label>
															<p
																id="performance-last-year"
																className={`text-button w-fit`}
															>
																{`${mergedActionData.result?.prevYear?.value?.toFixed(2) ?? "--"}${targetUnit === "%" || targetUnit === "" ? "" : " "}${targetUnit}`}
															</p>
														</div>
													</div>
													<div className={`grid h-80 grid-cols-1`}>
														{isDone == true ? (
															<SeempPrevYearTrendPlot
																trends={
																	mergedActionData.result
																		?.prevYear
																}
															/>
														) : (
															<LoadingStatus
																progressPercent={
																	seempProgress?.progressPercent
																}
																isDone={isDone}
															/>
														)}
													</div>
												</div>
											</div>
										) : null
										// <InputContainer
										// 	id="custom-label"
										// 	label={t("TITLE")}
										// 	fillHeight={true}
										// >
										// 	<textarea
										// 		readOnly={areFieldsDisabled}
										// 		id="custom-label"
										// 		{...register("label", {
										// 			setValueAs: (v) => (v === "" ? null : v),
										// 		})}
										// 		onClick={(e) => {
										// 			areFieldsDisabled && setIsToastOpen(true)
										// 		}}
										// 		className={`${label === null ? "border-amber-500" : errors.label !== undefined ? "border-red-500" : "border-dipai-secondary-901/20"} ${areFieldsDisabled ? "cursor-help" : ""} text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-full min-h-12 rounded-sm border-1 p-2`}
										// 	/>
										// 	{label === null ? (
										// 		<ValidationMessage
										// 			type={"warning"}
										// 			validationMessage={t("PROVIDE A TITLE")}
										// 		/>
										// 	) : null}
										// 	{errors.label?.message !== undefined ? (
										// 		<ValidationMessage
										// 			type={"error"}
										// 			validationMessage={errors.label.message}
										// 		/>
										// 	) : null}
										// </InputContainer>
									}
									{/* MEASURES SECTION */}
									<div
										className={`border-dipai-secondary-901/20 text-dipai-secondary-901 w-full rounded-sm border-1 p-4`}
									>
										<p className={`text-button h-8 font-semibold`}>
											{t("MEASURES")}
										</p>
										<div className={`grid grid-cols-2 gap-4`}>
											<div className={`flex flex-col gap-2.5`}>
												<InputContainer
													id="how-to-target"
													label={
														actionData.actionType !== "custom"
															? t("HOW TO REACH PERFORMANCE TARGET")
															: t("HOW TO REACH TARGET")
													}
													fillHeight={true}
												>
													<textarea
														readOnly={areFieldsDisabled}
														id="how-to-target"
														{...register("howTo", {
															setValueAs: (v) =>
																v === "" ? null : v,
														})}
														onClick={(e) => {
															areFieldsDisabled &&
																setIsToastOpen(true)
														}}
														className={`${howTo === null ? "border-amber-500" : errors.howTo !== undefined ? "border-red-500" : "border-dipai-secondary-901/20"} ${areFieldsDisabled ? "cursor-help" : ""} text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-full min-h-12 rounded-sm border-1 p-2`}
													/>
													{howTo === null ? (
														<ValidationMessage
															type={"warning"}
															validationMessage={t(
																"PROVIDE A DESCRIPTION OF HOW TO REACH THE TARGET"
															)}
														/>
													) : null}
													{errors.howTo?.message !== undefined ? (
														<ValidationMessage
															type={"error"}
															validationMessage={errors.howTo.message}
														/>
													) : null}
												</InputContainer>
												<InputContainer
													label={t("RESPONSIBLE PERSON")}
													id="responsible-person"
												>
													<Controller
														name="responsible"
														control={control}
														rules={{
															required:
																"Please select a responsible person",
														}}
														render={({ field, fieldState }) => (
															<div
																className={`${areFieldsDisabled ? "cursor-help" : ""}`}
																onClick={(e) => {
																	areFieldsDisabled &&
																		setIsToastOpen(true)
																}}
															>
																<ReactSelect
																	{...field}
																	options={usersOptions ?? []}
																	placeholder={t(
																		"SELECT THE RESPONSIBLE PERSON"
																	)}
																	styles={styles(
																		errors.responsible?.message,
																		field.value,
																		areFieldsDisabled
																	)}
																	value={field.value}
																	onChange={(val) => {
																		field.onChange(val)
																	}}
																	isDisabled={areFieldsDisabled}
																	isSearchable={true}
																	isClearable={true}
																	menuPlacement="auto"
																	menuPortalTarget={document.body}
																/>
															</div>
														)}
													/>
													{responsible === null ? (
														<ValidationMessage
															type={"warning"}
															validationMessage={t(
																"ASSIGN A RESPONSIBLE PERSON"
															)}
														/>
													) : null}
													{errors.responsible?.message !== undefined ? (
														<ValidationMessage
															type={"error"}
															validationMessage={
																errors.responsible.message
															}
														/>
													) : null}
												</InputContainer>
											</div>
											<div className={`flex flex-col gap-1.5`}>
												<label
													htmlFor="measures-list-accordion"
													className={`text-tiny h-4`}
												>
													{t("MEASURES TO IMPLEMENT")}
												</label>
												<Accordion.Root
													className="flex w-full flex-col"
													type="multiple"
													value={[
														...(isInvestmentChecked
															? [`${actionData.id}_investment`]
															: []),
														...(isClientDialogChecked
															? [`${actionData.id}_clientDialog`]
															: []),
														...(isCrewTrainingChecked
															? [`${actionData.id}_crewTraining`]
															: []),
													]}
													onValueChange={(value) => {
														// Only allow closing via the accordion trigger, not by unchecking
													}}
												>
													<MeasureAccordionItem
														actionId={actionData.id}
														measureType="investment"
														control={control}
														label={t("INVESTMENT")}
														areFieldsDisabled={areFieldsDisabled}
														setToastType={setToastType}
														setIsToastOpen={setIsToastOpen}
														isCheckedMethods={{
															register:
																register("investment.isSelected"),
															setValue: (value: boolean) => {
																setValue(
																	"investment.isSelected",
																	value
																)
															},
															value: isInvestmentChecked,
														}}
														howToMethods={{
															register: register("investment.howTo", {
																setValueAs: (v) =>
																	v === "" ? null : v,
															}),
															value: investmentHowTo,
															errorMessage:
																errors.investment?.howTo?.message,
															warningMessage: t(
																"PROVIDE A DESCRIPTION OF THE INVESTMENT APPROACH"
															),
															setValue: (value: string | null) =>
																setValue("investment.howTo", value),
														}}
														deadlineMethods={{
															controlRef: "investment.deadline",
															value: investmentDeadline,
															errorMessage:
																errors.investment?.deadline
																	?.message,
															warningMessage: t(
																"PROVIDE THE DEADLINE FOR THE INVESTMENT"
															),
															setValue: (value: Date | null) =>
																setValue(
																	"investment.deadline",
																	value
																),
														}}
														investmentAmountMethods={{
															controlRef:
																"investment.investmentAmount",
															value: investmentAmount,
															errorMessage:
																errors.investment?.investmentAmount
																	?.message,
															warningMessage: t(
																"PROVIDE THE INVESTMENT AMOUNT"
															),
															setValue: (value: number | null) =>
																setValue(
																	"investment.investmentAmount",
																	value
																),
														}}
													/>
													<MeasureAccordionItem
														actionId={actionData.id}
														measureType="clientDialog"
														control={control}
														label={t("CLIENT DIALOG")}
														areFieldsDisabled={areFieldsDisabled}
														setToastType={setToastType}
														setIsToastOpen={setIsToastOpen}
														isCheckedMethods={{
															register:
																register("clientDialog.isSelected"),
															setValue: (value: boolean) => {
																setValue(
																	"clientDialog.isSelected",
																	value
																)
															},
															value: isClientDialogChecked,
														}}
														howToMethods={{
															register: register(
																"clientDialog.howTo",
																{
																	setValueAs: (v) =>
																		v === "" ? null : v,
																}
															),
															value: clientDialogHowTo,
															errorMessage:
																errors.clientDialog?.howTo?.message,
															warningMessage: t(
																"PROVIDE A DESCRIPTION OF THE CLIENT DIALOG APPROACH"
															),
															setValue: (value: string | null) =>
																setValue(
																	"clientDialog.howTo",
																	value
																),
														}}
														deadlineMethods={{
															controlRef: "clientDialog.deadline",
															value: clientDialogDeadline,
															errorMessage:
																errors.clientDialog?.deadline
																	?.message,
															warningMessage: t(
																"PROVIDE THE DEADLINE FOR CLIENT DIALOG"
															),
															setValue: (value: Date | null) =>
																setValue(
																	"clientDialog.deadline",
																	value
																),
														}}
													/>
													<MeasureAccordionItem
														actionId={actionData.id}
														measureType="crewTraining"
														control={control}
														label={t("CREW TRAINING")}
														areFieldsDisabled={areFieldsDisabled}
														setToastType={setToastType}
														setIsToastOpen={setIsToastOpen}
														isCheckedMethods={{
															register:
																register("crewTraining.isSelected"),
															setValue: (value: boolean) => {
																setValue(
																	"crewTraining.isSelected",
																	value
																)
															},
															value: isCrewTrainingChecked,
														}}
														howToMethods={{
															register: register(
																"crewTraining.howTo",
																{
																	setValueAs: (v) =>
																		v === "" ? null : v,
																}
															),
															value: crewTrainingHowTo,
															errorMessage:
																errors.crewTraining?.howTo?.message,
															warningMessage: t(
																"PROVIDE A DESCRIPTION OF THE CREW TRAINING APPROACH"
															),
															setValue: (value: string | null) =>
																setValue(
																	"crewTraining.howTo",
																	value
																),
														}}
														deadlineMethods={{
															controlRef: "crewTraining.deadline",
															value: crewTrainingDeadline,
															errorMessage:
																errors.crewTraining?.deadline
																	?.message,
															warningMessage: t(
																"PROVIDE THE DEADLINE FOR CREW TRAINING"
															),
															setValue: (value: Date | null) =>
																setValue(
																	"crewTraining.deadline",
																	value
																),
														}}
													/>
												</Accordion.Root>
											</div>
										</div>
									</div>
								</div>
								<Toast.Root
									className="data-[state=closed]:animate-hide data-[state=open]:animate-slide-in data-[swipe=end]:animate-swipe-out bg-dipai-secondary-800 shadow-card grid grid-cols-[auto_max-content] items-center gap-x-3 rounded-sm border-1 border-white p-3 text-white [grid-template-areas:_'title_action'_'description_action'] data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]"
									open={isToastOpen}
									onOpenChange={setIsToastOpen}
								>
									<Toast.Title className="text-button mb-1.5 font-medium text-white [grid-area:_title]">
										{toastType === "notInEditMode"
											? `The action is not in edit mode`
											: `This section is disabled`}
									</Toast.Title>
									<Toast.Description asChild>
										<p className="text-tiny m-0 text-white [grid-area:_description]">
											{toastType === "notInEditMode"
												? `Use the Edit button above for updating the action.`
												: `Mark the checkbox to edit these fields.`}
										</p>
									</Toast.Description>
									<Toast.Action
										className="[grid-area:_action]"
										asChild
										altText="Goto schedule to undo"
									>
										<IconButton
											variant={"filled-secondary"}
											size={"medium"}
										>
											<IconButton.Icon>{MdClose}</IconButton.Icon>
										</IconButton>
									</Toast.Action>
								</Toast.Root>
								<Toast.Viewport
									ref={toastRef}
									className={`${useWindowBottom ? "fixed right-0 bottom-0" : "absolute right-0 bottom-0"} z-2147483647 m-0 flex w-97.5 max-w-[100vw] list-none flex-col gap-2.5 p-[calc(var(--viewport-padding)+1px+0.5rem)] py-[calc(var(--viewport-padding)/2+1px+0.5rem)] outline-none [--viewport-padding:_1.5rem]`}
								/>
							</Toast.Provider>
						</Accordion.Content>
					</Accordion.Item>
				</>
			)}
		</div>
	)
}

const MeasureAccordionItem = ({
	actionId,
	label,
	measureType,
	control,
	areFieldsDisabled,
	setToastType,
	setIsToastOpen,
	isCheckedMethods,
	howToMethods,
	deadlineMethods,
	investmentAmountMethods,
}: {
	actionId: number
	label: string
	measureType: "investment" | "clientDialog" | "crewTraining"
	control: Control<z.infer<typeof SeempActionFormSchema>>
	areFieldsDisabled: boolean
	setToastType: Dispatch<SetStateAction<"notInEditMode" | "notChecked">>
	setIsToastOpen: Dispatch<SetStateAction<boolean>>
	isCheckedMethods: {
		value: boolean
		register: UseFormRegisterReturn
		setValue: (value: boolean) => void
	}
	howToMethods: {
		register: UseFormRegisterReturn
		warningMessage: string
		errorMessage: string | undefined
		value: string | null
		setValue: (value: string | null) => void
	}
	deadlineMethods: {
		controlRef: "crewTraining.deadline" | "clientDialog.deadline" | "investment.deadline"
		warningMessage: string
		errorMessage: string | undefined
		value: Date | null
		setValue: (value: Date | null) => void
	}
	investmentAmountMethods?: {
		controlRef: "investment.investmentAmount"
		warningMessage: string
		errorMessage: string | undefined
		value: number | null
		setValue: (value: number | null) => void
	}
}) => {
	const { t } = useTranslation()

	const isFieldReadOnly = areFieldsDisabled || !isCheckedMethods.value // doesnt apply to checkbox
	const fieldCursorStyle = isFieldReadOnly ? "cursor-help" : "" // doesnt apply to checkbox

	const fieldBorderStyle = (
		value: number | Date | string | null,
		errorMessage: string | undefined
	) =>
		isCheckedMethods.value === true && value === null
			? "border-amber-500"
			: isCheckedMethods.value === true && errorMessage !== undefined
				? "border-red-500"
				: "border-dipai-secondary-901/20"

	const accordionWarningStyle =
		isCheckedMethods.value === true &&
		(howToMethods.value === null ||
			deadlineMethods.value === null ||
			investmentAmountMethods?.value === null)
			? "warning"
			: isCheckedMethods.value === true &&
				  (howToMethods.errorMessage !== undefined ||
						deadlineMethods.errorMessage !== undefined ||
						investmentAmountMethods?.errorMessage !== undefined)
				? "error"
				: "default"

	return (
		<Accordion.Item
			value={`${actionId}_${measureType}`}
			className={`${accordionWarningStyle === "warning" ? "border-amber-500" : accordionWarningStyle === "error" ? "border-red-500" : "border-dipai-secondary-901/20 not-last:border-b-0"} overflow-hidden rounded-t-sm border-1`}
		>
			<Accordion.Header
				className={`${accordionWarningStyle === "warning" ? "hover:bg-amber-800/5 data-[state=open]:bg-amber-900/5" : accordionWarningStyle === "error" ? "hover:bg-red-800/5 data-[state=open]:bg-red-900/5" : "hover:bg-dipai-secondary-800/5 data-[state=open]:bg-dipai-secondary-800/5"} flex h-11 p-3`}
			>
				<Accordion.Trigger
					className={
						"group grid w-full grid-cols-[1.25rem_1fr_1.25rem] gap-2 outline-none"
					}
				>
					<input
						type="checkbox"
						readOnly={areFieldsDisabled}
						className={`${areFieldsDisabled ? "cursor-help" : ""} h-4 w-4`}
						{...isCheckedMethods.register}
						onClick={(e) => {
							e.stopPropagation()
							if (areFieldsDisabled === true) {
								e.preventDefault()
								isCheckedMethods.setValue(isCheckedMethods.value)
								setIsToastOpen(true)
							} else {
								if (isCheckedMethods.value === true) {
									if (
										window.confirm(
											"Are you sure you want to remove this measures? This will also remove the description and deadline."
										) === false
									) {
										e.preventDefault()
										return
									}
								}
								howToMethods.setValue(null)
								deadlineMethods.setValue(null)
								investmentAmountMethods?.setValue(null)
								isCheckedMethods.setValue(!isCheckedMethods.value)
							}
						}}
					/>
					<p className={`text-dipai-secondary-901 text-tiny h-5 text-left`}>{label}</p>
					<div className={`flex items-center justify-center`}>
						<MdChevronLeft
							className="text-dipai-secondary-901 group-hover:text-dipai-secondary-600 h-5 w-5 rotate-180 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-hover:scale-110 group-hover:cursor-pointer group-data-[state=open]:rotate-270"
							aria-hidden
						/>
					</div>
				</Accordion.Trigger>
			</Accordion.Header>
			<Accordion.Content
				className={
					"data-[state=closed]:animate-slide-accordion-up data-[state=open]:animate-slide-accordion-down grid w-full gap-2.5 overflow-hidden p-3"
				}
			>
				<InputContainer
					id={`description-${measureType}`}
					label={t("DESCRIPTION")}
				>
					<textarea
						id={`description-${measureType}`}
						{...howToMethods.register}
						readOnly={isFieldReadOnly}
						onClick={(e) => {
							if (areFieldsDisabled === true) {
								setToastType("notInEditMode")
								setIsToastOpen(true)
							} else if (!isCheckedMethods.value) {
								setToastType("notChecked")
								setIsToastOpen(true)
							}
						}}
						className={`${fieldCursorStyle} ${fieldBorderStyle(howToMethods.value, howToMethods.errorMessage)} hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 text-tiny h-10 min-h-12 rounded-sm border-1 p-2`}
					/>
					{isCheckedMethods.value === true && howToMethods.value === null ? (
						<ValidationMessage
							type={"warning"}
							validationMessage={howToMethods.warningMessage}
						/>
					) : null}
					{isCheckedMethods.value === true && howToMethods.errorMessage !== undefined ? (
						<ValidationMessage
							type={"error"}
							validationMessage={howToMethods.errorMessage}
						/>
					) : null}
				</InputContainer>
				<div className={`grid grid-cols-2 gap-2.5`}>
					<InputContainer
						id={`deadline-${measureType}`}
						label={t("IMPLEMENTATION DEADLINE")}
					>
						<Controller
							name={deadlineMethods.controlRef}
							control={control}
							rules={{ required: false }}
							render={({ field }) => (
								<div
									id="calendar-portal"
									onClick={(e) => {
										if (areFieldsDisabled === true) {
											setToastType("notInEditMode")
											setIsToastOpen(true)
										} else if (!isCheckedMethods.value) {
											setToastType("notChecked")
											setIsToastOpen(true)
										}
									}}
								>
									<DatePicker
										ref={field.ref}
										id={`deadline-${measureType}`}
										portalId="calendar-portal"
										calendarContainer={CustomCalendarContainer}
										customInput={
											<CustomDatePickerInput
												cursorStyle={fieldCursorStyle}
												borderStyle={fieldBorderStyle(
													deadlineMethods.value,
													deadlineMethods.errorMessage
												)}
											/>
										}
										fixedHeight
										popperPlacement="bottom-start"
										dateFormat={"dd/MM/yyyy"}
										onChange={(date) => {
											field.onChange(date)
										}}
										minDate={new Date()}
										startDate={null}
										readOnly={isFieldReadOnly}
										selected={deadlineMethods.value}
										showTimeSelect={false}
										shouldCloseOnSelect={true}
									/>
								</div>
							)}
						/>
						{isCheckedMethods.value === true && deadlineMethods.value === null ? (
							<ValidationMessage
								type={"warning"}
								validationMessage={deadlineMethods.warningMessage}
							/>
						) : null}
						{isCheckedMethods.value === true &&
						deadlineMethods.errorMessage !== undefined ? (
							<ValidationMessage
								type={"error"}
								validationMessage={deadlineMethods.errorMessage}
							/>
						) : null}
					</InputContainer>
					{investmentAmountMethods !== undefined ? (
						<InputContainer
							id={`amount-${measureType}`}
							label={t("INVESTMENT AMOUNT")}
						>
							<Controller
								name={investmentAmountMethods.controlRef}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<CurrencyInput
										intlConfig={{ locale: "no-NO", currency: "EUR" }}
										// suffix=" €"
										// prefix="€"
										id={`amount-${measureType}`}
										ref={field.ref}
										allowDecimals={false}
										min={1}
										value={
											field.value === null ||
											field.value === undefined ||
											typeof field.value !== "number"
												? ""
												: field.value
										}
										readOnly={isFieldReadOnly}
										onClick={(e) => {
											if (areFieldsDisabled === true) {
												setToastType("notInEditMode")
												setIsToastOpen(true)
											} else if (!isCheckedMethods.value) {
												setToastType("notChecked")
												setIsToastOpen(true)
											}
										}}
										onValueChange={(value, name, values) => {
											const number = Number(value)
											field.onChange(isNaN(number) === false ? number : null)
										}}
										className={`${fieldCursorStyle} ${fieldBorderStyle(investmentAmountMethods.value, investmentAmountMethods.errorMessage)} text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 rounded-sm border-1 p-2`}
									/>
								)}
							/>
							{isCheckedMethods.value === true &&
							investmentAmountMethods.value === null ? (
								<ValidationMessage
									type={"warning"}
									validationMessage={investmentAmountMethods.warningMessage}
								/>
							) : null}
							{isCheckedMethods.value === true &&
							investmentAmountMethods.errorMessage !== undefined ? (
								<ValidationMessage
									type={"error"}
									validationMessage={investmentAmountMethods.errorMessage}
								/>
							) : null}
						</InputContainer>
					) : null}
				</div>
			</Accordion.Content>
		</Accordion.Item>
	)
}

const InputContainer = ({
	id,
	label,
	fillHeight = false,
	children,
}: {
	id: string
	label: string
	fillHeight?: boolean
	children: React.ReactNode
}) => {
	return (
		<div className={`flex ${fillHeight === true ? "h-full" : ""} flex-col gap-1.5`}>
			<label
				htmlFor={id}
				className={`text-tiny h-4`}
			>
				{label}
			</label>
			<div className={`flex h-full flex-col gap-0.5`}>{children}</div>
		</div>
	)
}

type ValidationType = "warning" | "error"
const validationColors: Record<ValidationType, { text: string; bg: string }> = {
	warning: { text: "text-amber-700", bg: "bg-amber-100" },
	error: { text: "text-red-700", bg: "bg-red-50" },
}
const validationIcon: Record<ValidationType, ReactElement> = {
	warning: (
		<ImportantIcon
			className="h-4 w-4"
			bgColor="text-amber-300"
			iconColor="text-amber-700"
		/>
	),
	error: (
		<ImportantIcon
			className="h-4 w-4"
			bgColor="text-red-300"
			iconColor="text-red-700"
		/>
	),
}
export const ValidationMessage = ({
	type,
	validationMessage,
}: {
	type: ValidationType
	validationMessage: string
}) => {
	return (
		<div
			className={`${validationColors[type].bg} flex w-full flex-row items-center gap-2 rounded-sm p-1 px-2`}
		>
			{validationIcon[type]}
			<p className={`text-tiny ${validationColors[type].text}`}>{validationMessage}</p>
		</div>
	)
}

type CustomCalendarInputProps = InputHTMLAttributes<HTMLInputElement> & {
	error?: boolean
	isCalendarOpen?: boolean
	cursorStyle: string
	borderStyle: string
}

export const CustomDatePickerInput = forwardRef<HTMLInputElement, CustomCalendarInputProps>(
	({ width = 124, cursorStyle, borderStyle, value, isCalendarOpen, ...props }, ref) => {
		return (
			<>
				<MdEvent className={`text-dipai-secondary-800 absolute top-2 ml-2`} />
				<input
					{...props}
					className={`${cursorStyle} ${borderStyle} text-tiny h-8 rounded-sm border-1 p-2 ${isCalendarOpen === false ? "" : "react-datepicker-ignore-onclickoutside"} pl-8.25 text-end ${
						props.disabled !== true
							? "hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500"
							: "bg-slate-100"
					}`}
					style={{ width: width }}
					ref={ref}
					type="text"
					value={value}
				/>
			</>
		)
	}
)

function LoadingStatus({
	progressPercent,
	isDone,
}: {
	progressPercent: number | undefined
	isDone: boolean
}) {
	const { t } = useTranslation()

	const percent = isDone !== true ? (progressPercent ?? 0) : 100
	return (
		<div className={`relative`}>
			<div className={`absolute h-80 w-full animate-pulse rounded-sm border-0 bg-gray-300`} />
			<div className={`relative flex w-full flex-col items-center`}>
				<LoadingCircle
					size="medium"
					containerHeightWithUnit="320px"
				/>
				<p className="absolute top-50">
					{`${t("CALCULATING TREND")}: ${percent.toFixed(0)}%`}
				</p>
			</div>
		</div>
	)
}
